import { gql } from '@apollo/client';
import { COMPLETED_TASK_FRAGMENT_WITH_TASK, TASK_FORM_ITEM_FRAGMENT } from '../../../common/form/task-form/model/task-items.gql';

export const GET_VOLUNTEER_COMMON_PROFILE = gql`
  query GetVolunteerCommonProfile($profileId: String!, $userId: String!, $limit: Int, $offset: Int, $isHistorical: Boolean) {
    vm {
      activityRoles {
        ...VolunteerCommonActivityRole
      }
      privilegedPrograms: programs(hasRule: { rule: ReadAllVolunteers }) {
        programId
      }
      profileTask {
        taskId
        title
        description
        order
          cake {
          cakeType
          title
        }
        taskItems {
          ...TaskFormItem
        }
      }
      volunteer(profileId: $profileId) {
        ...VolunteerCommonProfile
      }
      hasActivityRoles
      communicationTemplates {
        communicationTypeId
        name
        status
        type
        isHidden
      }
    }
    em {
      emails(userId: $userId) {
        ...VolunteerCommonProfileEmail
      }
      outboundSms(userId: $userId) {
        ...VolunteerCommonProfileSms
      }
    }
  }
  ${TASK_FORM_ITEM_FRAGMENT}

  fragment VolunteerCommonProfile on VOLUNTEER_VolunteerType {
    volunteerId
    volunteerStatistics {
      ...MyElementsSideBarVolunteerStatistics
    }
    profile {
      userId
      profileId
      preferredName
      firstName
      lastName
      email
      avatarUrl
      importOperation {
        importOperationId
        importedBy {
          profile {
            firstName
            lastName
          }
        }
        dateCreated
      }
      addedBy {
        profile {
          firstName
          lastName
        }
      }
      dateOfBirth
      description
      contactNumber
      completedProfileTask {
        ...CompletedTaskWithTask
      }
      completedTasks(limit: $limit, offset: $offset) {
        completedTaskId
        dateSubmitted
        status
        task {
          taskId
          title
          adminOnly
        }
      }
      metadata {
        totalCompletedTasks
      }
      additionalVolunteerHours
    }
    
    lastTeamRemovalDate
    dateCreated
    dateDeactivated
    tasks {
      taskId
      title
      order
      adminOnly
      isRefereeCheckTask
      isEventTask
      cake {
        cakeType
      }
      dateRemoved
    }
    flagging {
      dateFlagged
    }
    programs {
      programId
      description
      name
    }
    rosterStatus
    activityRoles {
      ...VolunteerCommonActivityRole
    }
    unavailabilities {
      volunteerUnavailabilityId
      startDate
      endDate
    }
    activityEnrolments(isHistorical: $isHistorical) {
      ...VolunteerCommonProfileActivityEnrolment
    }
    activityApplications {
      ...VolunteerCommonProfileActivityApplication
    }
    activityWaitlistings {
      ...VolunteerCommonProfileActivityWaitlisting
    }
    rosterings {
      publishedRosteringId
      smsLogs {
        __typename
        smsLogId
        messageId
        sentAt
        messageData
        direction
      }
    }
    flagging {
      dateFlagged
      flaggedReason
      flaggedBy {
        profile {
          profileId
          preferredName
          lastName
          avatarUrl
        }
      }
    }
    currentOnboardingStep {
      ...VolunteerCommonOnboardingStep
    }
    onboardingSteps {
      ...VolunteerCommonOnboardingStep
    }
    application {
      ...VolunteerCommonProfileApplication
    }
  }

  ${COMPLETED_TASK_FRAGMENT_WITH_TASK}

  fragment VolunteerCommonActivityRole on VOLUNTEER_ActivityRoleType {
    activityRoleId
    icon
    name
  }

  fragment VolunteerCommonBaseVolunteer on VOLUNTEER_VolunteerType {
    volunteerId
    profile {
      preferredName
      lastName
      email
      avatarUrl
      description
    }
    rosterStatus
    lastTeamRemovalDate
    dateCreated
    activityEnrolments {
      activityEnrolmentId
      activity {
        name
        program {
          name
        }
      }
    }
    activityRoles {
      activityRoleId
    }
    programs {
      programId
    }
    currentOnboardingStep {
      ...VolunteerCommonOnboardingStep
    }
    onboardingSteps {
      ...VolunteerCommonOnboardingStep
    }
    dateDeactivated
    flagging {
      dateFlagged
    }
  }

  fragment VolunteerCommonOnboardingStep on VOLUNTEER_VolunteerOnboardingStepType {
    volunteerOnboardingStepId
    stepCompletionDate
    volunteerOnboardingStepType {
      volunteerOnboardingStepTypeId
      order
    }
  }
  fragment VolunteerCommonProfileActivityEnrolment on VOLUNTEER_ActivityEnrolmentType {
    activityEnrolmentId
    dateRemoved
    dateEnrolled
    enrolledBy {
      profile {
        profileId
        preferredName
        firstName
        lastName
      }
    }
    availabilities {
      ...VolunteerCommonProfileActivityAvailability
    }
    unavailabilities {
      ...VolunteerCommonProfileActivityUnavailability
    }
    activity {
      activityId
      name
      nextX(x: 8)
      ... on VOLUNTEER_NonRecurringActivityType {
        __typename
      }
      startTime
      endTime
      startDate
      endDate
      isRestrictedActivity
      isActive
      isSuspended
      sessions {
        sessionId
      }
      activityLocation {
        placesAddress {
          description
        }
      }
      program {
        programId
        name
      }
      publishedRosters {
        rosterId
        activityDate
        rosterings {
          publishedRosteringId
          dateRemoved
          volunteer {
            volunteerId
          }
        }
      }
      ... on VOLUNTEER_RecurringActivityType {
        schedule {
          recurrences {
            humanReadable
            startDate
            endDate
          }
        }
      }
      activityReports {
        activityDate
        sessionReports {
          sessionReportId
        }
      }
      cancelledActivities {
        ...VolunteerCommonCancelledActivity
      }
      closedActivity {
        ...VolunteerCommonClosedActivity
      }
      draftRosters {
        ...VolunteerCommonDraftRoster
      }
    }
  }
  fragment VolunteerCommonProfileActivityAvailability on VOLUNTEER_ActivityAvailabilityType {
    activityDate
    isActive
    activityRoles {
      activityRoleId
      name
    }
  }
  fragment VolunteerCommonProfileActivityUnavailability on VOLUNTEER_ActivityUnavailabilityType {
    activityDate
    isActive
  }
  fragment VolunteerCommonProfileActivityApplication on VOLUNTEER_ActivityApplicationType {
    activityApplicationId
    activity {
      activityId
      name
      program {
        programId
        name
      }
    }
  }
  fragment VolunteerCommonProfileActivityWaitlisting on VOLUNTEER_ActivityWaitlistingType {
    activityWaitlistingId
    activity {
      activityId
      name
      program {
        programId
        name
      }
    }
  }

  fragment VolunteerCommonProfileAvailableActivity on VOLUNTEER_ActivityType {
    activityId
    name
    startTime
    endTime
    activityLocation {
      placesAddress {
        description
      }
    }
    program {
      programId
      name
    }
  }

  fragment VolunteerCommonProfileApplication on VOLUNTEER_ApplicationType {
    applicationId
    name
    email
    dateSubmitted
    # todo: set-up-your-profile
    # documents {
    #   applicantDocumentId
    #   volunteerDocumentType {
    #     volunteerDocumentTypeId
    #     name
    #   }
    #   resourceUrl
    #   submission {
    #     ...Submission
    #   }
    # }
    # formSubmissions {
    #   applicantSubmissionId
    #   submission {
    #     ...Submission
    #   }
    # }
    screeningAnswers {
      screeningAnswerId
      answer
      isCorrect
    }
  }

  fragment VolunteerCommonProfileEmail on EMAIL_PostmarkOutboundEmailType {
    __typename
    postmarkEmailId
    userId
    toEmail
    subject
    sentBy
    sentAt
    tag
    body
    postmarkClicks {
      clickedAt
    }
    postmarkBounce {
      bouncedAt
    }
    postmarkDelivery {
      deliveryAt
    }
    postmarkOpens {
      openedAt
    }
    postmarkSpamComplaint {
      spamComplaintAt
    }
    communicationTemplate {
      name
    }
  }
  
  fragment VolunteerCommonCancelledActivity on VOLUNTEER_CancelledActivityType {
    cancelledActivityId
    activityDate
  }
  fragment VolunteerCommonClosedActivity on VOLUNTEER_ClosedActivityType {
    closedActivityId
  }
  fragment VolunteerCommonDraftRoster on VOLUNTEER_DraftRosterType {
    draftRosterId
    activityDate
    publishedRoster {
      rosterId
      dateRemoved
    }
  }
  fragment MyElementsSideBarVolunteerStatistics on VOLUNTEER_VolunteerStatisticsType {
    attended
    sessionReportStatistics {
      ...MyElementsSideBarSessionReportStatistics
    }
  }
  fragment MyElementsSideBarSessionReportStatistics on VOLUNTEER_SessionReportStatisticsType {
    operationalHours
    activityLocations {
      activityLocationId
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_TASKS = gql`
  query GetVolunteerProfileTasks($userId: String!) {
    vm {
      profile(userId: $userId) {
        profileId
        completedTasks {
          completedTaskId
          dateLastUpdated
          task {
            adminOnly
            taskId
            title
          }
        }
      }
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_COMMUNICATION_TEMPLATES = gql`
  query GetVolunteerProfileCommunicationTemplates {
    vm {
      communicationTemplates {
        ...CommunicationTemplate
      }
    }
    im {
      communicationTemplates {
        ...CommunicationTemplate
      }
    }
  }
  fragment CommunicationTemplate on VOLUNTEER_CommunicationTemplateType {
    communicationTypeId
    name
    tag
  }
`;

